import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { FilterIcon } from "./icons";
import {
  COLUMN_KEYS,
  COLUMN_LABELS,
  DEFAULT_COLUMNS,
  DEFAULT_FILTERS,
  loadSavedFilters,
  persistSavedFilters,
  sanitizeColumnPrefs,
} from "./models";

function ChipInput({ values = [], onChange, placeholder }) {
  const [text, setText] = useState("");
  const commit = (raw) => {
    const parts = String(raw || "")
      .split(/[,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const next = Array.from(new Set([...(values || []), ...parts]));
    onChange?.(next);
    setText("");
  };
  return (
    <div>
      <input
        className="sfh-drawer-input"
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit(text);
          }
        }}
        onBlur={() => commit(text)}
      />
      {values?.length ? (
        <div className="sfh-chip-row">
          {values.map((v) => (
            <span key={v} className="sfh-chip">
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                onClick={() => onChange?.(values.filter((x) => x !== v))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RangeMinMax({ minVal, maxVal, onMin, onMax, prefix = "" }) {
  return (
    <div className="sfh-minmax">
      <div>
        {prefix ? <span>{prefix}</span> : null}
        <input type="number" placeholder="Min" value={minVal} onChange={(e) => onMin(e.target.value)} />
      </div>
      <span className="sfh-range-separator">to</span>
      <div>
        {prefix ? <span>{prefix}</span> : null}
        <input type="number" placeholder="Max" value={maxVal} onChange={(e) => onMax(e.target.value)} />
      </div>
    </div>
  );
}

function Section({ id, title, openId, setOpenId, children }) {
  const open = openId === id;
  return (
    <section className={`sfh-filter-section${open ? " open" : ""}`}>
      <button
        type="button"
        className="sfh-filter-section-header"
        onClick={() => setOpenId(open ? null : id)}
      >
        <span>{title}</span>
        <span>{open ? "⌃" : "⌄"}</span>
      </button>
      <div className="sfh-filter-section-content">{children}</div>
    </section>
  );
}

/**
 * Filter drawer — Ezisell HTML shell + SmartScout-style Data/Columns UX.
 * SmartScout capture files were not on disk; patterns follow the request + HTML.
 */
export function StorefrontFilters({
  open,
  onClose,
  filters,
  columns,
  brandOptions = [],
  categoryOptions = [],
  onApply,
}) {
  const [tab, setTab] = useState("data");
  const [draft, setDraft] = useState(filters);
  const [draftCols, setDraftCols] = useState(columns);
  const [openId, setOpenId] = useState("presets");
  const [categorySearch, setCategorySearch] = useState("");
  const [saved, setSaved] = useState([]);
  const [activePreset, setActivePreset] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraft({ ...DEFAULT_FILTERS, ...filters });
    setDraftCols(sanitizeColumnPrefs({ ...DEFAULT_COLUMNS, ...columns }));
    setSaved(loadSavedFilters());
    setTab("data");
  }, [open, filters, columns]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filteredCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return categoryOptions;
    return categoryOptions.filter((c) => String(c).toLowerCase().includes(q));
  }, [categoryOptions, categorySearch]);

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const applyPreset = (id) => {
    setActivePreset(id);
    if (id === "new") setDraft((d) => ({ ...d, newProductAge: "7", newProductsOnly: true }));
    if (id === "demand") setDraft((d) => ({ ...d, monthlySoldMin: "100" }));
    if (id === "competition") setDraft((d) => ({ ...d, sellersMax: "3" }));
    if (id === "wholesale") setDraft((d) => ({ ...d, monthlySoldMin: "50", sellersMax: "5" }));
  };

  const reset = () => {
    setDraft({ ...DEFAULT_FILTERS });
    setDraftCols({ ...DEFAULT_COLUMNS });
    setActivePreset(null);
  };

  const apply = () => {
    onApply?.({ filters: draft, columns: sanitizeColumnPrefs(draftCols) });
    onClose?.();
  };

  const savePreset = () => {
    const name = window.prompt("Name this filter:");
    if (!name?.trim()) return;
    const next = [{ id: `saved-${Date.now()}`, name: name.trim(), filters: draft }, ...saved].slice(0, 20);
    persistSavedFilters(next);
    setSaved(next);
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className="sfh-filter-overlay" onClick={onClose} data-testid="sfh-filter-overlay" />
      <aside className="sfh-filter-drawer" data-testid="sfh-filters">
        <header className="sfh-filter-drawer-header">
          <div className="sfh-filter-title">
            <div className="sfh-filter-title-icon">
              <FilterIcon className="h-4 w-4" />
            </div>
            <div>
              <h2>Filters</h2>
              <p>Refine loaded products from this seller</p>
            </div>
          </div>
          <button type="button" className="sfh-drawer-close" onClick={onClose} aria-label="Close filters">
            <X className="h-4 w-4 mx-auto" />
          </button>
        </header>

        <div className="sfh-filter-tabs">
          <button
            type="button"
            className={`sfh-filter-tab${tab === "data" ? " active" : ""}`}
            onClick={() => setTab("data")}
            data-testid="sfh-filter-tab-data"
          >
            Data
          </button>
          <button
            type="button"
            className={`sfh-filter-tab${tab === "columns" ? " active" : ""}`}
            onClick={() => setTab("columns")}
            data-testid="sfh-filter-tab-columns"
          >
            Columns
          </button>
        </div>

        {tab === "data" ? (
          <div className="sfh-filter-scroll" data-testid="sfh-filter-data">
            <Section id="presets" title="Presets & Saved" openId={openId} setOpenId={setOpenId}>
              <div className="sfh-preset-grid">
                {[
                  ["wholesale", "Wholesale Opportunity"],
                  ["new", "Top New Products"],
                  ["demand", "High Demand"],
                  ["competition", "Low Competition"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`sfh-preset-chip${activePreset === id ? " active" : ""}`}
                    onClick={() => applyPreset(id)}
                  >
                    {label}
                  </button>
                ))}
                {saved.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="sfh-preset-chip"
                    onClick={() => setDraft({ ...DEFAULT_FILTERS, ...s.filters })}
                  >
                    {s.name}
                  </button>
                ))}
                <button type="button" className="sfh-preset-chip" onClick={savePreset}>
                  Save current…
                </button>
              </div>
            </Section>

            <Section id="title" title="Product Title" openId={openId} setOpenId={setOpenId}>
              <input
                className="sfh-drawer-input"
                placeholder="Enter keywords"
                value={draft.productTitle}
                onChange={(e) => setField("productTitle", e.target.value)}
              />
              <label className="sfh-field-label">Exclude keywords</label>
              <ChipInput
                values={draft.excludeKeywords || []}
                onChange={(v) => setField("excludeKeywords", v)}
                placeholder="Words to exclude (Enter/comma)"
              />
            </Section>

            <Section id="ids" title="Product IDs" openId={openId} setOpenId={setOpenId}>
              <label className="sfh-field-label">UPC / GTIN</label>
              <ChipInput
                values={draft.upcList || []}
                onChange={(v) => setField("upcList", v)}
                placeholder="Enter UPC/GTIN"
              />
              <label className="sfh-field-label">Walmart Item ID</label>
              <ChipInput
                values={draft.itemIdList || []}
                onChange={(v) => setField("itemIdList", v)}
                placeholder="Enter Item IDs"
              />
            </Section>

            <Section id="brands" title="Brand" openId={openId} setOpenId={setOpenId}>
              <ChipInput
                values={draft.brands || []}
                onChange={(v) => setField("brands", v)}
                placeholder="Add brand (Enter/comma)"
              />
              <div className="sfh-check-list mt-2">
                {brandOptions.map((b) => {
                  const active = (draft.brands || []).includes(b);
                  return (
                    <label key={b}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() =>
                          setField(
                            "brands",
                            active
                              ? (draft.brands || []).filter((x) => x !== b)
                              : [...(draft.brands || []), b]
                          )
                        }
                      />
                      <span>{b}</span>
                      <small />
                    </label>
                  );
                })}
              </div>
            </Section>

            <Section id="category" title="Category" openId={openId} setOpenId={setOpenId}>
              <input
                className="sfh-drawer-input"
                placeholder="Search Category"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />
              <div className="sfh-check-list mt-2">
                <label>
                  <input
                    type="checkbox"
                    checked={
                      filteredCategories.length > 0 &&
                      filteredCategories.every((c) => (draft.categories || []).includes(c))
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setField(
                          "categories",
                          Array.from(new Set([...(draft.categories || []), ...filteredCategories]))
                        );
                      } else {
                        const drop = new Set(filteredCategories);
                        setField(
                          "categories",
                          (draft.categories || []).filter((c) => !drop.has(c))
                        );
                      }
                    }}
                  />
                  <span>Select All</span>
                  <small />
                </label>
                {filteredCategories.map((c) => {
                  const active = (draft.categories || []).includes(c);
                  return (
                    <label key={c}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() =>
                          setField(
                            "categories",
                            active
                              ? (draft.categories || []).filter((x) => x !== c)
                              : [...(draft.categories || []), c]
                          )
                        }
                      />
                      <span>{c}</span>
                      <small />
                    </label>
                  );
                })}
              </div>
            </Section>

            <Section id="storePrice" title="Storefront Price" openId={openId} setOpenId={setOpenId}>
              <RangeMinMax
                prefix="$ "
                minVal={draft.storefrontPriceMin}
                maxVal={draft.storefrontPriceMax}
                onMin={(v) => setField("storefrontPriceMin", v)}
                onMax={(v) => setField("storefrontPriceMax", v)}
              />
            </Section>

            <Section id="buyBox" title="Walmart Buy Box Price" openId={openId} setOpenId={setOpenId}>
              <RangeMinMax
                prefix="$ "
                minVal={draft.buyBoxMin}
                maxVal={draft.buyBoxMax}
                onMin={(v) => setField("buyBoxMin", v)}
                onMax={(v) => setField("buyBoxMax", v)}
              />
            </Section>

            <Section id="maxCost" title="Max Cost" openId={openId} setOpenId={setOpenId}>
              <RangeMinMax
                prefix="$ "
                minVal={draft.maxCostMin}
                maxVal={draft.maxCostMax}
                onMin={(v) => setField("maxCostMin", v)}
                onMax={(v) => setField("maxCostMax", v)}
              />
            </Section>

            <Section id="monthly" title="Estimated Monthly Units Sold" openId={openId} setOpenId={setOpenId}>
              <RangeMinMax
                minVal={draft.monthlySoldMin}
                maxVal={draft.monthlySoldMax}
                onMin={(v) => setField("monthlySoldMin", v)}
                onMax={(v) => setField("monthlySoldMax", v)}
              />
            </Section>

            <Section id="sellers" title="Seller Count" openId={openId} setOpenId={setOpenId}>
              <RangeMinMax
                minVal={draft.sellersMin}
                maxVal={draft.sellersMax}
                onMin={(v) => setField("sellersMin", v)}
                onMax={(v) => setField("sellersMax", v)}
              />
            </Section>

            <Section id="rating" title="Rating" openId={openId} setOpenId={setOpenId}>
              <select
                className="sfh-drawer-select"
                value={draft.ratingMin}
                onChange={(e) => setField("ratingMin", e.target.value)}
              >
                <option value="">Any rating</option>
                <option value="3">3.0+</option>
                <option value="3.5">3.5+</option>
                <option value="4">4.0+</option>
                <option value="4.5">4.5+</option>
              </select>
            </Section>

            <Section id="reviews" title="Review Count" openId={openId} setOpenId={setOpenId}>
              <RangeMinMax
                minVal={draft.reviewCountMin || ""}
                maxVal={draft.reviewCountMax || ""}
                onMin={(v) => setField("reviewCountMin", v)}
                onMax={(v) => setField("reviewCountMax", v)}
              />
            </Section>

            <Section id="availability" title="Availability / In Stock" openId={openId} setOpenId={setOpenId}>
              <select
                className="sfh-drawer-select"
                value={draft.availability}
                onChange={(e) => setField("availability", e.target.value)}
              >
                <option value="Any">Any</option>
                <option value="In stock">In stock</option>
                <option value="Out of stock">Out of stock</option>
              </select>
            </Section>

            <Section id="fulfillment" title="Fulfillment" openId={openId} setOpenId={setOpenId}>
              <select
                className="sfh-drawer-select"
                value={draft.fulfillment || "Any"}
                onChange={(e) => setField("fulfillment", e.target.value)}
              >
                <option value="Any">Any</option>
                <option value="WFS">WFS</option>
                <option value="FBM">Seller Fulfilled</option>
              </select>
            </Section>

            <Section id="sources" title="Source Availability" openId={openId} setOpenId={setOpenId}>
              <label className="sfh-column-option">
                <input
                  type="checkbox"
                  checked={!!draft.hasBuySources}
                  onChange={(e) => setField("hasBuySources", e.target.checked)}
                />
                <span>Has Buy Sources</span>
              </label>
            </Section>

            <Section id="new" title="New Product" openId={openId} setOpenId={setOpenId}>
              <select
                className="sfh-drawer-select"
                value={draft.newProductAge || ""}
                onChange={(e) => {
                  setField("newProductAge", e.target.value);
                  setField("newProductsOnly", Boolean(e.target.value));
                }}
              >
                <option value="">Any date</option>
                <option value="1">Added today</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
              </select>
            </Section>
          </div>
        ) : (
          <div className="sfh-filter-scroll" data-testid="sfh-filter-columns">
            <div className="sfh-column-list">
              {COLUMN_KEYS.map((key) => (
                <label key={key} className="sfh-column-option">
                  <input
                    type="checkbox"
                    checked={!!draftCols[key]}
                    onChange={(e) => setDraftCols((c) => ({ ...c, [key]: e.target.checked }))}
                  />
                  <span>{COLUMN_LABELS[key]}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <footer className="sfh-filter-footer">
          <button type="button" className="sfh-filter-reset" onClick={reset} data-testid="sfh-filter-reset">
            Reset
          </button>
          <button type="button" className="sfh-filter-apply" onClick={apply} data-testid="sfh-filter-apply">
            Apply Filters
          </button>
        </footer>
      </aside>
    </>,
    document.body
  );
}
